import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { getMakeupAnalysisJob, searchProducts } from '../api/endpoints';
import { useAtlas } from '../atlas/AtlasCell';
import { makeupCoords } from '../atlas/coords';
import { MakeupResult } from '../analysis/PremiumResult';
import Loading from '../components/Loading';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import { navigateTo } from '../utils/navigation';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import type { MakeupAnalysisJob, ProductSearchResultItem } from '../types/api';
import { useReportExport } from '../reports/useReportExport';

const TEAL = '#2AA4A6';
const EASE: [number, number, number, number] = [0.22, 0.61, 0.36, 1];

/* ── Helpers ─────────────────────────────────────────────────────────── */

function getMakeupProductIcon(t: string): string {
  const l = t.toLowerCase();
  if (l.includes('foundation') || l.includes('concealer')) return 'face_retouching_natural';
  if (l.includes('eye') || l.includes('mascara') || l.includes('liner')) return 'visibility';
  if (l.includes('lip') || l.includes('gloss')) return 'blur_circular';
  if (l.includes('blush') || l.includes('bronzer') || l.includes('highlight')) return 'wb_sunny';
  if (l.includes('primer')) return 'layers';
  if (l.includes('powder') || l.includes('setting')) return 'grain';
  if (l.includes('brush') || l.includes('sponge')) return 'brush';
  return 'palette';
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="row-3" style={{ marginBottom: 'var(--s-4)' }}>
      <span style={{ width: 4, height: 18, borderRadius: 99, background: TEAL, flexShrink: 0 }} />
      <h2 className="t-label" style={{ margin: 0 }}>{children}</h2>
    </div>
  );
}

function ProductTypeCard({ type, hint, onClick }: { type: string; hint: string; onClick: () => void }) {
  return (
    <button className="product-type-card" onClick={onClick}>
      <div className="product-type-card__icon">
        <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>{getMakeupProductIcon(type)}</span>
      </div>
      <div className="product-type-card__body">
        <p className="product-type-card__name">{type}</p>
        <p className="product-type-card__hint">{hint}</p>
      </div>
      <div className="product-type-card__arrow">
        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>arrow_forward</span>
      </div>
    </button>
  );
}

/* ── Page ────────────────────────────────────────────────────────────── */

export default function MakeupAnalysisResultPage(): React.JSX.Element {
  const { exporting, exportReport, errorToast } = useReportExport();
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<MakeupAnalysisJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProductType, setSelectedProductType] = useState<string | null>(null);
  const [productResults, setProductResults] = useState<ProductSearchResultItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const prefersReduced = useReducedMotion();
  const pageIn: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: prefersReduced ? 0 : 0.38, ease: EASE } },
  };

  useEffect(() => {
    if (!id) return;
    getMakeupAnalysisJob(id)
      .then(res => { setJob(res.job); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [id]);

  // Fetch the generated atlas once; the sheet crops its photo cells from it.
  const { atlasUrl } = useAtlas(job?.result_image_path);

  const handleProductClick = async (type: string) => {
    setSelectedProductType(type);
    setLoadingProducts(true);
    setProductResults([]);
    try {
      const results = await searchProducts({ product_keywords: type, limit: 10 });
      setProductResults(results);
    } catch { /* empty state shown */ }
    finally { setLoadingProducts(false); }
  };

  if (loading) return <Loading />;

  if (!job) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-8)', textAlign: 'center', minHeight: '60vh' }}>
        <h2 className="t-h2" style={{ margin: '0 0 var(--s-3)' }}>Analysis not found</h2>
        <p className="t-small t-mute" style={{ marginBottom: 'var(--s-6)' }}>We couldn&apos;t locate this makeup analysis report.</p>
        <button onClick={() => { navigateTo('/'); }} className="btn btn--primary">Back to Dashboard</button>
      </div>
    );
  }

  if (job.status !== 'done') {
    navigateTo(`/makeup-analysis/${id}/pending`);
    return <Loading />;
  }

  const result = job.result;
  const shortId = id?.slice(0, 8).toUpperCase() ?? '';
  const dateStr = new Date(job.submitted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const recommendations = result?.recommended_product_types?.filter((p) => p.trim().length > 0) ?? [];

  return (
    <motion.div initial="hidden" animate="visible" variants={pageIn} className="stack-7"
      style={{ maxWidth: 560, marginInline: 'auto', width: '100%', paddingBottom: 'var(--s-10)' }}>

      {/* ── Premium result composition (the prototype layout) ── */}
      {result ? (
        <MakeupResult
          result={result}
          atlasUrl={atlasUrl}
          coords={makeupCoords}
          imagePath={job.image_path}
          id={shortId}
          date={dateStr}
          onOpenPoster={() => { if (id) void exportReport('makeup', id); }}
          exporting={exporting}
        />
      ) : (
        <div className="card card--bordered card--flat" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
          <p className="t-small t-mute">Analysis details unavailable.</p>
        </div>
      )}
      {errorToast}

      {/* ── Product Recommendations ── */}
      {recommendations.length > 0 && (
        <section className="stack-5">
          <SectionLabel>Recommended for You</SectionLabel>
          <p className="t-small t-mute" style={{ marginTop: 0 }}>Products matched to your makeup profile — click any to browse real results.</p>
          <motion.div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--s-5)' }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
          >
            {recommendations.map((type, i) => (
              <motion.div key={i} variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}>
                <ProductTypeCard type={type} hint="Matched to your makeup profile." onClick={() => handleProductClick(type)} />
              </motion.div>
            ))}
          </motion.div>
        </section>
      )}

      <AIDisclaimerFooter generatedPreviews />

      {/* ── Product search modal ── */}
      {selectedProductType && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div className="modal" style={{ zIndex: 110 }}>
          <div
            className="modal__backdrop"
            role="button"
            tabIndex={0}
            aria-label="Close dialog"
            onClick={() => { setSelectedProductType(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedProductType(null);
              }
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.18, ease: EASE }}
            className="modal__card modal__card--lg"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
          >
            <div className="modal__header">
              <h3 className="modal__title" style={{ textTransform: 'capitalize' }}>{selectedProductType}</h3>
              <button onClick={() => { setSelectedProductType(null); }} className="btn btn--icon" aria-label="Close">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
              </button>
            </div>
            <div className="modal__scroll">
              {loadingProducts ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s-9)' }}>
                  <span className="material-symbols-outlined t-mute animate-spin" style={{ fontSize: '32px' }}>progress_activity</span>
                </div>
              ) : productResults.length > 0 ? (
                <div className="stack-3">
                  {productResults.map((product, idx) => (
                    <a key={idx} href={product.product_link} target="_blank" rel="noreferrer" className="product-result">
                      <div className="product-result__inner">
                        {product.product_img_url ? (
                          <img src={product.product_img_url} alt={product.product_name} className="product-result__img" style={{ objectFit: 'contain' }} />
                        ) : (
                          <div className="product-result__img">
                            <span className="material-symbols-outlined">image</span>
                          </div>
                        )}
                        <div className="product-result__body">
                          <p className="product-result__name">{product.product_name}</p>
                          {product.product_price && (
                            <p className="product-result__price">
                              {typeof product.product_price === 'number' ? `$${product.product_price}` : product.product_price}
                            </p>
                          )}
                          <p className="product-result__desc">{product.product_description ?? 'No description available.'}</p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-9)', textAlign: 'center', gap: 'var(--s-4)' }}>
                  <div className="icon-ring">
                    <span className="material-symbols-outlined">inventory_2</span>
                  </div>
                  <p className="t-small t-mute">No products found for this category.</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>,
        document.body
      )}

    </motion.div>
  );
}
